var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { createComponentStatus, updateComponentStatus, setComponentStatusAsDefault, deleteComponentStatus, getComponentStatuses, batchReorderComponentStatuses } from '../services/ComponentStatusesService';
// Query Keys
export const COMPONENT_STATUSES_QUERY_KEYS = {
    all: ['componentStatuses'],
    list: () => ['componentStatuses', 'list']
};
// Hook to create a component status
export function useCreateComponentStatusMutation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => __awaiter(this, void 0, void 0, function* () {
            const response = yield createComponentStatus(data);
            if (response.error) {
                throw new Error(response.error.message);
            }
            return response.data;
        }),
        onMutate: (newStatus) => __awaiter(this, void 0, void 0, function* () {
            // Cancel any outgoing refetches to avoid overwriting our optimistic update
            yield queryClient.cancelQueries({ queryKey: COMPONENT_STATUSES_QUERY_KEYS.list() });
            // Snapshot the previous value
            const previousStatuses = queryClient.getQueryData(COMPONENT_STATUSES_QUERY_KEYS.list());
            // Optimistically update to the new value with temporary ID
            if (previousStatuses) {
                const optimisticStatus = {
                    id: Date.now(), // Temporary ID
                    name: newStatus.status,
                    status: newStatus.status,
                    color: newStatus.color,
                    description: newStatus.description || '',
                    isSystemStatus: false,
                    editUrl: '',
                    deleteUrl: '',
                    active: true,
                    isDefault: false,
                    position: previousStatuses.length + 1,
                    componentsCount: 0,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString()
                };
                queryClient.setQueryData(COMPONENT_STATUSES_QUERY_KEYS.list(), [...previousStatuses, optimisticStatus]);
            }
            // Return a context object with the snapshotted value
            return { previousStatuses };
        }),
        onSuccess: () => {
            toast.success('Component status created successfully!');
            void queryClient.invalidateQueries({
                queryKey: COMPONENT_STATUSES_QUERY_KEYS.all
            });
        },
        onError: (error, _newStatus, context) => {
            // If the mutation fails, use the context returned from onMutate to roll back
            if (context === null || context === void 0 ? void 0 : context.previousStatuses) {
                queryClient.setQueryData(COMPONENT_STATUSES_QUERY_KEYS.list(), context.previousStatuses);
            }
            toast.error(error.message || 'Failed to create component status. Please try again.');
        },
        onSettled: () => {
            // Always refetch after error or success to ensure consistency
            void queryClient.invalidateQueries({
                queryKey: COMPONENT_STATUSES_QUERY_KEYS.all
            });
        }
    });
}
// Hook to update a component status
export function useUpdateComponentStatusMutation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (_a) => __awaiter(this, [_a], void 0, function* ({ id, data }) {
            const response = yield updateComponentStatus(id, data);
            if (response.error) {
                throw new Error(response.error.message);
            }
            return response.data;
        }),
        onMutate: (_a) => __awaiter(this, [_a], void 0, function* ({ id, data }) {
            // Cancel any outgoing refetches to avoid overwriting our optimistic update
            yield queryClient.cancelQueries({ queryKey: COMPONENT_STATUSES_QUERY_KEYS.list() });
            // Snapshot the previous value
            const previousStatuses = queryClient.getQueryData(COMPONENT_STATUSES_QUERY_KEYS.list());
            // Optimistically update to the new value
            if (previousStatuses) {
                const updatedStatuses = previousStatuses.map((status) => {
                    if (status.id === id) {
                        return Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, status), (data.status && { status: data.status, name: data.status })), (data.color && { color: data.color })), (data.description !== undefined && { description: data.description })), { updatedAt: new Date().toISOString() });
                    }
                    return status;
                });
                queryClient.setQueryData(COMPONENT_STATUSES_QUERY_KEYS.list(), updatedStatuses);
            }
            // Return a context object with the snapshotted value
            return { previousStatuses };
        }),
        onSuccess: () => {
            toast.success('Component status updated successfully!');
            void queryClient.invalidateQueries({
                queryKey: COMPONENT_STATUSES_QUERY_KEYS.all
            });
        },
        onError: (error, _variables, context) => {
            // If the mutation fails, use the context returned from onMutate to roll back
            if (context === null || context === void 0 ? void 0 : context.previousStatuses) {
                queryClient.setQueryData(COMPONENT_STATUSES_QUERY_KEYS.list(), context.previousStatuses);
            }
            toast.error(error.message || 'Failed to update component status. Please try again.');
        },
        onSettled: () => {
            // Always refetch after error or success to ensure consistency
            void queryClient.invalidateQueries({
                queryKey: COMPONENT_STATUSES_QUERY_KEYS.all
            });
        }
    });
}
// Hook to delete a component status
export function useDeleteComponentStatusMutation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => __awaiter(this, void 0, void 0, function* () {
            const response = yield deleteComponentStatus(id);
            if (response.error) {
                throw new Error(response.error.message);
            }
        }),
        onMutate: (id) => __awaiter(this, void 0, void 0, function* () {
            // Cancel any outgoing refetches to avoid overwriting our optimistic update
            yield queryClient.cancelQueries({ queryKey: COMPONENT_STATUSES_QUERY_KEYS.list() });
            // Snapshot the previous value
            const previousStatuses = queryClient.getQueryData(COMPONENT_STATUSES_QUERY_KEYS.list());
            // Optimistically remove the status from the list
            if (previousStatuses) {
                const filteredStatuses = previousStatuses.filter((status) => status.id !== id);
                queryClient.setQueryData(COMPONENT_STATUSES_QUERY_KEYS.list(), filteredStatuses);
            }
            // Return a context object with the snapshotted value
            return { previousStatuses };
        }),
        onSuccess: () => {
            toast.success('Component status deleted successfully!');
            void queryClient.invalidateQueries({
                queryKey: COMPONENT_STATUSES_QUERY_KEYS.all
            });
        },
        onError: (error, _id, context) => {
            // If the mutation fails, use the context returned from onMutate to roll back
            if (context === null || context === void 0 ? void 0 : context.previousStatuses) {
                queryClient.setQueryData(COMPONENT_STATUSES_QUERY_KEYS.list(), context.previousStatuses);
            }
            toast.error(error.message || 'Failed to delete component status. Please try again.');
        },
        onSettled: () => {
            // Always refetch after error or success to ensure consistency
            void queryClient.invalidateQueries({
                queryKey: COMPONENT_STATUSES_QUERY_KEYS.all
            });
        }
    });
}
// Hook to set component status as default
export function useSetComponentStatusAsDefaultMutation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => __awaiter(this, void 0, void 0, function* () {
            const response = yield setComponentStatusAsDefault(id);
            if (response.error) {
                throw new Error(response.error.message);
            }
            return response.data;
        }),
        onMutate: (id) => __awaiter(this, void 0, void 0, function* () {
            // Cancel any outgoing refetches to avoid overwriting our optimistic update
            yield queryClient.cancelQueries({ queryKey: COMPONENT_STATUSES_QUERY_KEYS.list() });
            // Snapshot the previous value
            const previousStatuses = queryClient.getQueryData(COMPONENT_STATUSES_QUERY_KEYS.list());
            // Optimistically update to set the new default
            if (previousStatuses) {
                const updatedStatuses = previousStatuses.map((status) => (Object.assign(Object.assign({}, status), { isDefault: status.id === id })));
                queryClient.setQueryData(COMPONENT_STATUSES_QUERY_KEYS.list(), updatedStatuses);
            }
            // Return a context object with the snapshotted value
            return { previousStatuses };
        }),
        onSuccess: () => {
            toast.success('Default component status updated successfully!');
            void queryClient.invalidateQueries({
                queryKey: COMPONENT_STATUSES_QUERY_KEYS.all
            });
        },
        onError: (error, _id, context) => {
            // If the mutation fails, use the context returned from onMutate to roll back
            if (context === null || context === void 0 ? void 0 : context.previousStatuses) {
                queryClient.setQueryData(COMPONENT_STATUSES_QUERY_KEYS.list(), context.previousStatuses);
            }
            toast.error(error.message || 'Failed to set default component status. Please try again.');
        },
        onSettled: () => {
            // Always refetch after error or success to ensure consistency
            void queryClient.invalidateQueries({
                queryKey: COMPONENT_STATUSES_QUERY_KEYS.all
            });
        }
    });
}
// Hook to fetch component statuses
export function useComponentStatuses() {
    return useQuery({
        queryKey: COMPONENT_STATUSES_QUERY_KEYS.list(),
        queryFn: () => __awaiter(this, void 0, void 0, function* () {
            const response = yield getComponentStatuses();
            if (response.error) {
                throw new Error(response.error.message);
            }
            return response.data || [];
        }),
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: 3
    });
}
// Hook to reorder component statuses
export function useReorderComponentStatusesMutation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (reorderedStatuses) => __awaiter(this, void 0, void 0, function* () {
            // Collect all position updates that changed
            const updates = reorderedStatuses
                .map((status, index) => {
                const newPosition = index + 1;
                return {
                    id: status.id,
                    position: newPosition,
                    hasChanged: status.position !== newPosition
                };
            })
                .filter((update) => update.hasChanged)
                .map(({ id, position }) => ({ id, position }));
            // Only send request if there are actual changes
            if (updates.length === 0) {
                return;
            }
            // Send batch update request
            const result = yield batchReorderComponentStatuses(updates);
            if (result.error) {
                throw new Error(result.error.message);
            }
        }),
        onMutate: (reorderedStatuses) => __awaiter(this, void 0, void 0, function* () {
            // Cancel any outgoing refetches
            yield queryClient.cancelQueries({ queryKey: COMPONENT_STATUSES_QUERY_KEYS.list() });
            // Snapshot the previous value
            const previousStatuses = queryClient.getQueryData(COMPONENT_STATUSES_QUERY_KEYS.list());
            // Optimistically update with new positions
            const updatedStatuses = reorderedStatuses.map((status, index) => (Object.assign(Object.assign({}, status), { position: index + 1 })));
            queryClient.setQueryData(COMPONENT_STATUSES_QUERY_KEYS.list(), updatedStatuses);
            // Return context with the snapshotted value
            return { previousStatuses };
        }),
        onSuccess: () => {
            toast.success('Component statuses reordered successfully!');
            void queryClient.invalidateQueries({
                queryKey: COMPONENT_STATUSES_QUERY_KEYS.all
            });
        },
        onError: (error, _variables, context) => {
            // Rollback on error
            if (context === null || context === void 0 ? void 0 : context.previousStatuses) {
                queryClient.setQueryData(COMPONENT_STATUSES_QUERY_KEYS.list(), context.previousStatuses);
            }
            toast.error(error.message || 'Failed to reorder component statuses. Please try again.');
        },
        onSettled: () => {
            // Always refetch after error or success
            void queryClient.invalidateQueries({
                queryKey: COMPONENT_STATUSES_QUERY_KEYS.all
            });
        }
    });
}
